import { SelectedOption, SelectedVariants } from '@/types'
import {
  Wrapper,
  Title,
  Button,
  OptionNameWrapper,
  OptionName,
  OptionRow,
  Price,
  Quantity,
  Session,
  OptionInfoWrapper,
  OptionPriceWrapper,
  ExtraGroup,
  GroupHeader,
  OptionType,
  ExtraGroupsContainer,
} from './OptionalExtras.styles'
import React, { useEffect, useState } from 'react'
import { Body, Spacing } from '@big-red-group/storefront-common-ui'
import Icon from './Icons'
import ToolTipWrapper from './ToolTipWrapper'
import { formatCurrency, OptionalExtra } from '@big-red-group/storefront-common-checkout'

type ExtraRecords = Record<
  number,
  { quantity: number; price: number; name: string; description?: string; optionId: number }
>

type OptionalExtrasProps = {
  sessions: SelectedVariants
  onQuantityChange: (selectedVariants: SelectedVariants, selectedOptions: SelectedOption[] | undefined) => void
  selectedOptions: SelectedOption[] | undefined
}

export const OptionalExtras: React.FC<OptionalExtrasProps> = ({ sessions, onQuantityChange, selectedOptions }) => {
  const [optionQuantities, setOptionQuantities] = useState<ExtraRecords>({})
  const [optionsExceedingExtraslimit, setOptionsExceedingExtraslimit] = useState<Map<number, number>>(new Map())
  useEffect(() => {
    loadPreviouslySelectedExtras()
  }, [])

  const convertToOptionalExtraArray = (extraRecords: ExtraRecords, optionId: number) => {
    let totalExtrasPerOption = 0
    const extras: OptionalExtra[] = []
    Object.keys(extraRecords).map((key) => {
      const extraRecord = extraRecords[parseInt(key)]
      const extra = {
        id: parseInt(key),
        price: extraRecord.price,
        quantity: extraRecord.quantity,
        name: extraRecord.name,
      }
      if (extraRecord.quantity > 0 && extraRecord.optionId == optionId) {
        totalExtrasPerOption = totalExtrasPerOption + extra.quantity
        extras.push(extra)
      }
    })

    optionsExceedingExtraslimit.set(optionId, totalExtrasPerOption)
    setOptionsExceedingExtraslimit(optionsExceedingExtraslimit)

    return extras
  }
  const loadPreviouslySelectedExtras = () => {
    const newOptionQuantities: ExtraRecords = {}
    selectedOptions?.map((option) => {
      option.metadata?.extras?.map((extra) => {
        newOptionQuantities[extra.id] = {
          ...extra,
          quantity: Math.min(option.quantity, extra.quantity),
          optionId: option.id,
        }
      })
      updateSelectedOptions(newOptionQuantities, option.id)
    })

    setOptionQuantities({ ...optionQuantities, ...newOptionQuantities })
  }
  const updateSelectedOptions = (newOptionQuantities: ExtraRecords, optionId: number) => {
    let currentOption: SelectedOption
    selectedOptions = selectedOptions?.map((selectedOption) => {
      if (selectedOption.allIds.includes(optionId)) {
        const extras = convertToOptionalExtraArray(newOptionQuantities, optionId)
        currentOption = {
          ...selectedOption,
          extras,
          metadata: { ...selectedOption.metadata, extras },
        }
        return currentOption
      }
      return selectedOption
    })

    Object.values(sessions).map((session) => {
      if (session.option.id === optionId) {
        session.option = { ...session.option, ...currentOption.extras, ...currentOption.metadata }
        return
      }
    })
    onQuantityChange(sessions, selectedOptions)
  }

  const handleOperation = (extra: OptionalExtra, optionId: number, operation: 'PLUS' | 'MINUS', limit: number) => {
    const currentQuantity = optionQuantities[extra.id]?.quantity || 0
    const quantity = operation === 'PLUS' ? Math.min(currentQuantity + 1, limit) : Math.max(currentQuantity - 1, limit)
    const newOptionQuantities: ExtraRecords = {
      ...optionQuantities,
      [extra.id]: {
        ...extra,
        optionId,
        quantity,
      },
    }

    setOptionQuantities(newOptionQuantities)
    updateSelectedOptions(newOptionQuantities, optionId)
  }

  return (
    <Wrapper>
      <Title>Optional extras</Title>
      {Object.values(sessions).map(({ option: { id: optionId, extrasGroups, name }, quantity }, index) => (
        <>
          <ExtraGroupsContainer key={optionId + '-' + index}>
            {quantity > 0 && extrasGroups && (
              <>
                {extrasGroups?.map(({ id: groupId, multiple, extras }, idx) => (
                  <ExtraGroup key={idx}>
                    <GroupHeader>
                      <Body weight="medium">
                        Select{' '}
                        {multiple ? (
                          <mark data-testid="select-title-any">any</mark>
                        ) : (
                          <mark data-testid="select-title-one">one</mark>
                        )}{' '}
                        per {name != 'Per Person' ? name.toLowerCase() : 'participant'}
                      </Body>
                    </GroupHeader>
                    {extras?.map((extra, idx) => (
                      <OptionRow key={`${extra.id}-${idx}`} data-testid={`option-row-${extra.id}`}>
                        <OptionInfoWrapper>
                          <OptionNameWrapper>
                            <ToolTipWrapper data={extra.description}>
                              <Icon name="question" width={18} height={18} />
                            </ToolTipWrapper>
                            <OptionName>{extra.name}</OptionName>
                          </OptionNameWrapper>
                        </OptionInfoWrapper>
                        <OptionPriceWrapper>
                          <Price>{formatCurrency(extra.price)}</Price>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              onClick={() => handleOperation(extra, optionId, 'MINUS', 0)}
                              disabled={
                                optionQuantities[extra.id]?.quantity === 0 || optionQuantities[extra.id] === undefined
                              }
                              data-testid={`selector-minus-${extra.id}`}
                            >
                              -
                            </Button>
                            <Quantity data-testid={`extras-quantity-${extra.id}`}>
                              {optionQuantities[extra.id]?.quantity || 0}
                            </Quantity>
                            <Button
                              onClick={() => handleOperation(extra, optionId, 'PLUS', quantity)}
                              disabled={
                                multiple
                                  ? optionQuantities[extra.id]?.quantity === quantity
                                  : (optionsExceedingExtraslimit.get(optionId) as number) >= quantity
                              }
                              data-testid={`selector-plus-${extra.id}`}
                            >
                              +
                            </Button>
                          </div>
                        </OptionPriceWrapper>
                      </OptionRow>
                    ))}
                  </ExtraGroup>
                ))}
              </>
            )}
          </ExtraGroupsContainer>
        </>
      ))}
    </Wrapper>
  )
}

export default OptionalExtras
